import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { apiUrl } from "../utils/api";
import { computeFormProgress, kanonisiereAntworten } from "@kmvs/forms-core";
import { stepFormSchema } from "../components/risikoanalyseStep/schema";
import type {
  Answers,
  FormSchema,
} from "../components/risikoanalyseStep/types";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { sectionColorHex } from "../components/risikoanalyseStep/sectionColor";
import TopBar from "../components/risikoanalyseStep/TopBar";
import Sidebar from "../components/risikoanalyseStep/Sidebar";
import BottomBar from "../components/risikoanalyseStep/BottomBar";
import QuestionBlock from "../components/risikoanalyseStep/QuestionBlock";
import WelcomeFlow from "../components/risikoanalyseStep/WelcomeFlow";
import SummaryView from "../components/risikoanalyseStep/SummaryView";
import DetailAnalysesView from "../components/risikoanalyseStep/DetailAnalysesView";
import AuswertungAbschluss from "../components/risikoanalyseStep/AuswertungAbschluss";
import {
  clearDraft,
  loadDraft,
  saveDraft,
} from "../components/risikoanalyseStep/draftStorage";
import {
  READONLY_UPLOAD_TARGET,
  UploadTargetProvider,
} from "../components/risikoanalyseStep/uploadTarget";
import type { UploadTarget } from "../components/risikoanalyseStep/uploadTarget";
import {
  dokumentKeysDesSchemas,
  holeAnalyseUploadsMitFrist,
  mitServerDokumenten,
} from "../services/risikoanalyseUploadService";
import type { DocumentRef } from "@kmvs/forms-core";
import { useAuth } from "../context/AuthContext";

const DEFAULT_WELCOME_LS_PREFIX = "risikoanalyse-step-welcome-seen:";
const DEFAULT_DRAFT_PREFIX = "risikoanalyse-step-draft:";
const SIDEBAR_WIDTH = 288;
const AUTOSAVE_DEBOUNCE_MS = 1500;

const useStyles = makeStyles({
  pageState: {
    minHeight: ["100vh", "100dvh"],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.ink,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
  },
  pageStateCard: {
    maxWidth: "480px",
    paddingTop: "32px",
    paddingBottom: "32px",
    paddingLeft: "24px",
    paddingRight: "24px",
    backgroundColor: "rgba(255,255,255,0.05)",
    textAlign: "center",
    ...shorthands.borderRadius("8px"),
  },
  pageStateTitle: {
    margin: 0,
    marginBottom: "8px",
    fontFamily: BRAND.fontAccent,
    fontSize: "1.8rem",
    color: BRAND.gold,
  },
  pageStateBody: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: "rgba(255,255,255,0.8)",
  },
  shell: {
    height: ["100vh", "100dvh"],
    maxHeight: ["100vh", "100dvh"],
    display: "flex",
    flexDirection: "column",
    backgroundColor: BRAND.bg,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    overflow: "hidden",
  },
  /**
   * Beratermodus is rendered inside the AppShell, whose <main> already
   * has a definite height (flex: 1 inside a 100vh column). 100% lets the
   * form fit that container exactly instead of leaking 100vh past the
   * AppShell header.
   */
  shellEmbedded: {
    height: "100%",
    maxHeight: "100%",
  },
  body: {
    flexGrow: 1,
    display: "flex",
    overflow: "hidden",
    minHeight: 0,
  },
  sidebarDesktop: {
    display: "none",
    flexShrink: 0,
    width: `${SIDEBAR_WIDTH}px`,
    overflowY: "auto",
    ...shorthands.borderRight("1px", "solid", "rgba(45,20,43,0.1)"),
    "@media (min-width: 1024px)": {
      display: "block",
    },
  },
  rightPane: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
  },
  drawer: {
    position: "fixed",
    inset: 0,
    zIndex: 30,
    display: "flex",
    "@media (min-width: 1024px)": {
      display: "none",
    },
  },
  drawerBackdrop: {
    flexGrow: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  drawerInner: {
    width: `${SIDEBAR_WIDTH}px`,
    maxWidth: "85%",
    backgroundColor: BRAND.ink,
  },
  main: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    minHeight: 0,
  },
  inner: {
    width: "100%",
    boxSizing: "border-box",
    maxWidth: "780px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "24px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: "32px",
    flexGrow: 1,
    "@media (min-width: 768px)": {
      paddingLeft: "32px",
      paddingRight: "32px",
    },
  },
  innerWide: {
    maxWidth: "1024px",
  },
  sectionPill: {
    display: "inline-block",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    marginBottom: "8px",
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    marginTop: "4px",
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
    fontWeight: 400,
    color: BRAND.ink,
    lineHeight: 1.15,
  },
  description: {
    marginTop: "8px",
    marginBottom: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  questions: {
    marginTop: "16px",
  },
  /** Hinweis, wenn die Unterlagen-Liste des Servers nicht angekommen ist. */
  unterlagenHinweis: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    flexWrap: "wrap",
    marginTop: "16px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    lineHeight: 1.4,
    color: "#8a5a00",
    backgroundColor: "rgba(214,158,46,0.12)",
    ...shorthands.padding("10px", "12px"),
    ...shorthands.borderRadius("8px"),
  },
  unterlagenHinweisKnopf: {
    flexShrink: 0,
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    ...shorthands.padding("6px", "12px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "rgba(138,90,0,0.4)"),
    ":disabled": {
      cursor: "default",
      opacity: 0.6,
    },
  },
  /**
   * Bottom bar wrapper: pinned at the bottom of the right pane only, so
   * the form sidebar on the left stays unobscured. The right pane is its
   * own scroll container, so this bar sits *below* the scroll area
   * rather than overlapping it.
   */
  bottomBarPinned: {
    flexShrink: 0,
    backgroundColor: BRAND.white,
    zIndex: 15,
    // iPad/iPhone Home-Indicator: Buttons nicht unter die Safe-Area schieben.
    // 0 auf Geräten ohne Home-Indicator (viewport-fit=cover ist gesetzt).
    paddingBottom: "env(safe-area-inset-bottom)",
    ...shorthands.borderTop("1px", "solid", "rgba(45,20,43,0.1)"),
  },
});

// --- component ------------------------------------------------------------

export type RisikoanalyseStepFormProps = {
  /** Optional alternate schema (e.g. the short version). Defaults to the full schema. */
  schema?: FormSchema;
  /** Storage prefixes to keep welcome-seen flag and drafts isolated per schema. */
  welcomeStoragePrefix?: string;
  draftStoragePrefix?: string;
  /** Version string sent to the backend on submit (debug/migration aid). */
  submitVersion?: string;
  /**
   * Internal/advisor (Beratermodus) mode:
   *  - skips the welcome page,
   *  - loads + writes against `/contact/:id/risikoanalyse/internal-state`
   *    instead of `/public-form/:token` — no PublicFormToken is involved,
   *  - on successful submit, navigates back to the contact page instead
   *    of the public form-hub.
   */
  internalMode?: boolean;
  /** Where to navigate after a successful internal-mode submit. */
  internalReturnPath?: string;
  /**
   * Required when internalMode is true. The contactId the Beratermodus
   * draft is anchored on.
   */
  internalContactId?: number | null;
};

const RisikoanalyseStepForm: React.FC<RisikoanalyseStepFormProps> = ({
  schema = stepFormSchema,
  welcomeStoragePrefix = DEFAULT_WELCOME_LS_PREFIX,
  draftStoragePrefix = DEFAULT_DRAFT_PREFIX,
  submitVersion = "step-form-v1",
  internalMode = false,
  internalReturnPath,
  internalContactId = null,
}) => {
  const styles = useStyles();
  const params = useParams();
  const token = internalMode ? null : params.token ?? null;
  // Storage key for autosave / welcome-seen: token when public, an internal
  // marker when Beratermodus. Both live under the same localStorage prefix.
  const persistKey: string | null = internalMode
    ? internalContactId != null
      ? `internal:${internalContactId}`
      : null
    : token;
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);
  /**
   * Der Kontakt trägt bereits eine bestätigte Auswertung
   * (`risikoanalyseAuswertungAt`). Steuert im Abschluss-Schritt nur die
   * Beschriftung — erneutes Bestätigen bleibt jederzeit möglich, weil eine
   * Korrektur nach der Übernahme der Normalfall ist.
   */
  const [bereitsBestaetigt, setBereitsBestaetigt] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [answers, setAnswers] = useState<Answers>({});

  const [showWelcome, setShowWelcome] = useState<boolean>(() => {
    // Beratermodus skips the customer welcome flow entirely.
    if (internalMode) return false;
    if (!persistKey) return true;
    try {
      return (
        window.localStorage.getItem(welcomeStoragePrefix + persistKey) !== "1"
      );
    } catch {
      return true;
    }
  });

  const [sectionIdx, setSectionIdx] = useState(0);
  const [subStepIdx, setSubStepIdx] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const autosaveTimerRef = useRef<number | null>(null);
  const draftLoadedRef = useRef(false);

  /**
   * Ist die Unterlagen-Liste des Servers angekommen?
   *
   * Ein Fehlschlag war bis hierher folgenlos UND unsichtbar: Der Berater sah
   * die Dateiliste des (womöglich veralteten) Entwurfs, hielt sie für den
   * Stand des Servers und schickte sie ab — von dort wandert sie in die
   * Auswertung. Erreichbar ist der Fall real: Der Fragebogen selbst kommt mit
   * `contacts:read` aus, die Liste prüft zusätzlich das Zeilenrecht am Kontakt
   * (403), und Netzabbruch oder abgelaufene Frist tun dasselbe.
   *
   * Solange dieser Zustand steht, wird der Slot zur reinen Anzeige (siehe
   * `uploadZiel`): Hochladen und Entfernen gegen eine unbekannte Liste
   * anzubieten hiesse, Dubletten und stille Verluste einzuladen.
   */
  const [unterlagenFehler, setUnterlagenFehler] = useState(false);
  const [unterlagenLaedt, setUnterlagenLaedt] = useState(false);

  /**
   * Die Unterlagen-Liste EINES Kontakts — mit Frist, aber ohne Fehlerschlucken.
   *
   * Die Frist (`holeAnalyseUploadsMitFrist`) ist der Grund, warum das Formular
   * nie an einer Dateiliste hängen bleibt; wer scheitert, meldet das dem
   * Aufrufer, statt still nichts zu tun.
   */
  const holeUnterlagen = async (
    contactId: number
  ): Promise<Record<string, DocumentRef[]>> => {
    const { dateien } = await holeAnalyseUploadsMitFrist(contactId);
    return dateien;
  };

  /** „Erneut versuchen" am Hinweis — legt die Liste nachträglich darüber. */
  const wiederholeUnterlagen = async () => {
    if (!internalMode || internalContactId == null) return;
    setUnterlagenLaedt(true);
    try {
      const dateien = await holeUnterlagen(internalContactId);
      setAnswers((prev) =>
        mitServerDokumenten(prev, dokumentKeysDesSchemas(schema), dateien)
      );
      setUnterlagenFehler(false);
    } catch {
      setUnterlagenFehler(true);
    } finally {
      setUnterlagenLaedt(false);
    }
  };

  /*
   * Load contact pre-fill + Beratermodus draft (or token data for customer mode).
   *
   * `abgebrochen` ist kein Schmuck: Der Lauf hängt an bis zu zwei Anfragen
   * (Kontakt-Zustand, danach die Unterlagen-Liste mit 8 s Frist). Wechselt der
   * Kontakt innerhalb derselben Route, liefe der ältere Lauf sonst zu Ende und
   * setzte zuletzt `setAnswers` — mit den Antworten UND den DocumentRefs des
   * VORIGEN Kontakts, die der Autosave dann unter dem neuen `persistKey`
   * wegschriebe.
   */
  useEffect(() => {
    let abgebrochen = false;
    const run = async () => {
      // Internal-Mode requires a contactId; customer mode requires a token.
      if (internalMode) {
        if (internalContactId == null) {
          setValid(false);
          setLoading(false);
          return;
        }
      } else if (!token) {
        setValid(false);
        setLoading(false);
        return;
      }
      setUnterlagenFehler(false);

      /*
       * Die Unterlagen-Liste PARALLEL zum Kontakt-Zustand anfordern.
       *
       * Gebraucht wird sie erst ganz unten (nach dem Zusammenbau aller
       * Entwurfsquellen), abgeholt wird sie aber sofort: Nacheinander addierten
       * sich beide Wartezeiten, und im schlechtesten Fall stand „Formular wird
       * geladen…" acht Sekunden länger. Der Fehlerfall wird hier schon
       * abgefangen, damit kein unbehandelter Rejection-Zweig entsteht.
       */
      const unterlagenLauf =
        internalMode && internalContactId != null
          ? holeUnterlagen(internalContactId).then(
              (dateien) => ({ ok: true as const, dateien }),
              () => ({ ok: false as const, dateien: {} as Record<string, DocumentRef[]> })
            )
          : null;

      try {
        let prefilledFromContact: Answers = {};
        let prefilledFromSubmission: Answers = {};

        if (internalMode) {
          // Beratermodus: single call returns contact pre-fill + draft.
          const res = await fetch(
            apiUrl(`contact/${internalContactId}/risikoanalyse/internal-state`)
          );
          if (abgebrochen) return;
          if (!res.ok) {
            setValid(false);
            return;
          }
          setValid(true);
          const payload = await res.json();
          const c = payload?.contact;
          if (c) {
            const display = c.displayName || c.lastname || "";
            setCompanyName(display);
            prefilledFromContact.name = display;
            if (c.address) {
              prefilledFromContact.strasse = c.address.street ?? "";
              prefilledFromContact.plz = c.address.postalCode ?? "";
              prefilledFromContact.stadt = c.address.city ?? "";
              prefilledFromContact.land = c.address.country ?? "";
            }
            prefilledFromContact.telefon = c.phone ?? "";
            prefilledFromContact.fax = c.fax ?? "";
            prefilledFromContact.mail = c.email ?? "";
            prefilledFromContact.webseite = c.website ?? "";
            // Eigentümer wird per Default mit dem aktuellen Kontakt vorbelegt;
            // der Picker erlaubt im Formular einen Wechsel oder Anlegen eines
            // Mini-Kontakts.
            const ownerId =
              typeof c.id === "number" ? c.id : Number(c.id) || null;
            if (ownerId) {
              prefilledFromContact.standort_eigentuemer = {
                contactId: ownerId,
                label: display,
                email: c.email ?? undefined,
              };
            }
          }
          /*
           * Branche und Spezialisierung stehen nicht am Kontakt, sondern als
           * eigene Zuordnungsliste daneben — deshalb neben `contact` und nicht
           * darin. Der Anzeigepfad kommt vom Server mit: Die Zusammenfassung
           * und das Portal haben den Katalog nicht und zeigten sonst eine
           * nackte Nummer.
           */
          if (Array.isArray(payload?.branchen) && payload.branchen.length > 0) {
            prefilledFromContact.branchen = payload.branchen;
          }
          /*
           * Drei mögliche Ausgangsstände, in dieser Rangfolge:
           *
           *  1. `finalData` — die bestätigte Auswertung. Sie ist das, was am
           *     Kontakt gilt; ein danach entstandener Entwurf existiert nicht,
           *     weil das Bestätigen der letzte Schritt ist. Ohne diesen Vorrang
           *     zeigte das Formular nach dem Übernehmen wieder den Stand VOR
           *     der letzten Korrektur.
           *  2. `draftData` — der eigene Entwurf des Beraters.
           *  3. `kundenAntworten` — was der Kunde über seinen Link geschickt
           *     hat. Genau das verspricht das Startfenster („Antworten des
           *     Kunden im großen Formular ergänzen") und hielt es bis zu
           *     diesem Stand nicht: Der Beratermodus las nur seinen eigenen
           *     Entwurf, die Einreichung lag im PublicFormToken und war allein
           *     auf der Auswertungsseite zu sehen. Wer dem Versprechen folgte,
           *     fand ein leeres Formular vor.
           *
           * Beide Ablageformen kommen vor: `{ answers, version }` aus dem
           * Schritt-Fragebogen und die flache Form älterer Einreichungen.
           */
          const antwortsatz = (roh: unknown): Answers | null => {
            if (!roh || typeof roh !== "object") return null;
            const o = roh as Record<string, unknown>;
            const inhalt =
              o.answers && typeof o.answers === "object" ? (o.answers as Answers) : (o as Answers);
            return kanonisiereAntworten(inhalt);
          };
          prefilledFromSubmission =
            antwortsatz(payload?.finalData) ??
            antwortsatz(payload?.draftData) ??
            antwortsatz(payload?.kundenAntworten) ??
            {};
          if (payload?.finalAt) setBereitsBestaetigt(true);
        } else {
          // Customer mode: token-based loading (unchanged).
          const res = await fetch(apiUrl(`public-form/${token}`));
          if (abgebrochen) return;
          if (!res.ok) {
            setValid(false);
            return;
          }
          setValid(true);
          try {
            const data = await res.json();
            const c = data?.contact;
            if (c) {
              const display = c.displayName || c.lastname || "";
              setCompanyName(display);
              prefilledFromContact.name = display;
              if (c.address) {
                prefilledFromContact.strasse = c.address.street ?? "";
                prefilledFromContact.plz = c.address.postalCode ?? "";
                prefilledFromContact.stadt = c.address.city ?? "";
                prefilledFromContact.land = c.address.country ?? "";
              }
              prefilledFromContact.telefon = c.phone ?? "";
              prefilledFromContact.fax = c.fax ?? "";
              prefilledFromContact.mail = c.email ?? "";
              prefilledFromContact.webseite = c.website ?? "";
              const ownerId =
                typeof c.id === "number" ? c.id : Number(c.id) || null;
              if (ownerId) {
                prefilledFromContact.standort_eigentuemer = {
                  contactId: ownerId,
                  label: display,
                  email: c.email ?? undefined,
                };
              }
            }
          } catch {
            // ignore
          }
        }

        // Existing local-draft (autosave) — overrides stammdaten pre-fill.
        //
        // Jede Quelle wird EINZELN kanonisiert und erst dann gemischt: Sonst
        // wuerde `kanonisiereAntworten` dem kanonischen Key den Vorrang geben
        // und ein aelterer Server-Entwurf koennte den neueren lokalen Entwurf
        // schlagen. So bleibt es bei „die spaetere Quelle gewinnt".
        let zusammengesetzt: Answers = {
          ...prefilledFromContact,
          ...prefilledFromSubmission,
        };
        if (persistKey) {
          const draft = loadDraft(persistKey, draftStoragePrefix);
          if (draft) {
            zusammengesetzt = {
              ...zusammengesetzt,
              ...kanonisiereAntworten(draft.answers),
            };
            setSectionIdx(draft.sectionIdx);
            setSubStepIdx(draft.subStepIdx);
            setDraftSavedAt(draft.savedAt);
          }
        }

        /**
         * Die Serverliste der Unterlagen gewinnt — und zwar GANZ zuletzt.
         *
         * Die Dateien liegen in der Datenbank; die `DocumentRef`-Liste im
         * Antwortsatz ist nur eine Kopie, und beide Entwurfsquellen darüber
         * können veraltet sein: der Kontakt-Entwurf, weil er nur beim Speichern
         * geschrieben wird, und der localStorage-Entwurf, weil er das Gerät
         * nicht verlässt — er gewinnt oben sogar gegen den Server-Entwurf. Lädt
         * ein Kollege am zweiten Rechner eine Bilanz hoch oder entfernt sie
         * jemand, zeigte dieses Formular sonst eine Liste, die es so nicht mehr
         * gibt, und schriebe sie beim nächsten Autosave zurück.
         *
         * Deshalb hier, NACH dem Zusammenbau aller Entwurfsquellen und VOR dem
         * ersten `setAnswers` (also vor dem ersten Autosave). Danach hält jeder
         * Upload-/Entfernen-Vorgang die Liste selbst aktuell, weil er die
         * Antwort des Servers übernimmt.
         *
         * Nur im Beratermodus: der öffentliche Token-Pfad hat keinen Bearer und
         * bekommt seinen eigenen Lesepfad mit AP7.
         *
         * ANGEFORDERT wurde die Liste bereits oben, parallel zum Kontakt-
         * Zustand; hier wird nur noch auf sie gewartet.
         */
        if (unterlagenLauf) {
          const ergebnis = await unterlagenLauf;
          if (ergebnis.ok) {
            zusammengesetzt = mitServerDokumenten(
              zusammengesetzt,
              dokumentKeysDesSchemas(schema),
              ergebnis.dateien
            );
          } else if (!abgebrochen) {
            /*
             * Das Formular bleibt benutzbar — ein `setValid(false)` machte aus
             * einer fehlenden Dateiliste eine unbenutzbare Analyse. Folgenlos
             * ist der Fehlschlag aber nicht mehr: `unterlagenFehler` zeigt ihn
             * am Formular an und nimmt den Slots das Schreibrecht, damit
             * niemand gegen eine unbekannte Liste hoch- oder ablädt.
             */
            setUnterlagenFehler(true);
          }
        }

        if (abgebrochen) return;
        setAnswers(zusammengesetzt);
        draftLoadedRef.current = true;
      } catch {
        if (!abgebrochen) setValid(false);
      } finally {
        if (!abgebrochen) setLoading(false);
      }
    };
    run();
    return () => {
      abgebrochen = true;
    };
  }, [token, internalMode, internalContactId]);

  // Debounced auto-save on any change (after initial load)
  useEffect(() => {
    if (!draftLoadedRef.current) return;
    if (!persistKey) return;
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = window.setTimeout(() => {
      saveDraft(
        persistKey,
        { answers, sectionIdx, subStepIdx },
        draftStoragePrefix
      );
      setDraftSavedAt(new Date().toISOString());
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimerRef.current)
        window.clearTimeout(autosaveTimerRef.current);
    };
  }, [answers, sectionIdx, subStepIdx, persistKey, draftStoragePrefix]);

  const handleAnswerChange = (key: string, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const totalSubSteps = useMemo(
    () => schema.sections.reduce((sum, s) => sum + s.subSteps.length, 0),
    [schema]
  );
  const flatIndex = useMemo(() => {
    let i = 0;
    for (let s = 0; s < sectionIdx; s += 1)
      i += schema.sections[s].subSteps.length;
    return i + subStepIdx;
  }, [sectionIdx, subStepIdx, schema]);
  /**
   * Wo „Absenden" steht.
   *
   * Nicht mehr schlicht der letzte Schritt: Im Beratermodus folgt danach noch
   * „Auswertung & Abschluss", und der bringt seinen eigenen Knopf mit
   * („Bestätigen & speichern"). Zwei Speichern-Knöpfe nebeneinander, von denen
   * einer den Entwurf und der andere die Übernahme schreibt, wären nicht
   * auseinanderzuhalten. Absenden steht deshalb einen Schritt früher und
   * springt anschliessend genau dorthin.
   */
  const abschlussFlatIndex = useMemo(() => {
    let lauf = 0;
    for (const section of schema.sections) {
      for (const sub of section.subSteps) {
        if (sub.customKind === "auswertung") return lauf;
        lauf += 1;
      }
    }
    return -1;
  }, [schema]);
  const aufAbschluss = abschlussFlatIndex >= 0 && flatIndex === abschlussFlatIndex;
  const isLastStep =
    abschlussFlatIndex >= 0
      ? flatIndex === abschlussFlatIndex - 1
      : flatIndex === totalSubSteps - 1;
  const canPrev = flatIndex > 0;

  const overallFraction = useMemo(
    () => computeFormProgress(schema, answers) / 100,
    [schema, answers]
  );
  const stepFraction = (flatIndex + 1) / totalSubSteps;

  const goPrev = () => {
    if (subStepIdx > 0) setSubStepIdx((s) => s - 1);
    else if (sectionIdx > 0) {
      const prev = sectionIdx - 1;
      setSectionIdx(prev);
      setSubStepIdx(schema.sections[prev].subSteps.length - 1);
    }
    mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goNext = () => {
    const sec = schema.sections[sectionIdx];
    if (subStepIdx + 1 < sec.subSteps.length) {
      setSubStepIdx((s) => s + 1);
    } else if (sectionIdx + 1 < schema.sections.length) {
      setSectionIdx((s) => s + 1);
      setSubStepIdx(0);
    }
    mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  /**
   * Springt auf den Abschluss-Schritt („Auswertung & Abschluss").
   *
   * Über den `customKind` gesucht und nicht über einen festen Index: Das
   * Schema wächst, und ein „letzter Schritt" per `length - 1` zeigte nach der
   * nächsten Erweiterung woandershin. Fehlt der Schritt — die Kurzform hat ihn
   * nicht —, bleibt die Anzeige stehen, statt irgendwohin zu springen.
   */
  const geheZuAbschluss = () => {
    for (const [si, section] of schema.sections.entries()) {
      const ssi = section.subSteps.findIndex((sub) => sub.customKind === "auswertung");
      if (ssi >= 0) {
        setSectionIdx(si);
        setSubStepIdx(ssi);
        mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }
  };

  const handleSelect = (s: number, ss: number) => {
    setSectionIdx(s);
    setSubStepIdx(ss);
    setMobileNavOpen(false);
    mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleWelcomeComplete = () => {
    setShowWelcome(false);
    if (persistKey) {
      try {
        window.localStorage.setItem(welcomeStoragePrefix + persistKey, "1");
      } catch {}
    }
  };

  const handleSaveDraft = async () => {
    if (!persistKey) return;
    setSavingDraft(true);
    try {
      saveDraft(
        persistKey,
        { answers, sectionIdx, subStepIdx },
        draftStoragePrefix
      );
      setDraftSavedAt(new Date().toISOString());
    } finally {
      // Tiny delay so the "Speichere…" label is visible briefly.
      window.setTimeout(() => setSavingDraft(false), 250);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const endpoint = internalMode
        ? apiUrl(`contact/${internalContactId}/risikoanalyse/internal-state`)
        : apiUrl(`public-form/${token}`);
      if (internalMode && internalContactId == null) return;
      if (!internalMode && !token) return;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers, version: submitVersion }),
      });
      if (res.ok) {
        if (persistKey) clearDraft(persistKey, draftStoragePrefix);
        if (internalMode) {
          /*
           * Im Beratermodus wird NICHT weggesprungen.
           *
           * Bis zu diesem Stand warf „Absenden" den Berater zurück auf den
           * Kontakt — mitten in der Arbeit, denn der eigentliche Abschluss
           * (Angaben in den Kontakt übernehmen, Detail-Risiken anlegen) kam
           * erst danach, auf einer eigenen Seite, die er über ein Startfenster
           * finden musste. Wer sie nicht kannte, hatte seine Analyse nie
           * übernommen.
           *
           * Jetzt bleibt er stehen und geht zum letzten Schritt weiter, wo
           * genau das passiert. `draftGespeichertAt` zeigt, dass gespeichert
           * wurde; der öffentliche Kundenweg endet unverändert im Form-Hub.
           */
          setDraftSavedAt(new Date().toISOString());
          geheZuAbschluss();
        } else if (token) {
          navigate(`/public/customer-form-hub/${token}`);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading / invalid / submitOk states
  if (loading) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <div className={styles.pageStateBody}>Formular wird geladen…</div>
        </div>
      </div>
    );
  }
  if (!valid) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <div className={styles.pageStateBody}>
            Der Link ist ungültig oder abgelaufen.
          </div>
        </div>
      </div>
    );
  }
  if (submitOk) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <h2 className={styles.pageStateTitle}>Vielen Dank</h2>
          <p className={styles.pageStateBody}>
            Ihre Risikoanalyse wurde übermittelt. Wir melden uns mit den
            nächsten Schritten.
          </p>
        </div>
      </div>
    );
  }

  if (showWelcome) {
    return (
      <WelcomeFlow
        companyName={companyName}
        onComplete={handleWelcomeComplete}
      />
    );
  }

  const section = schema.sections[sectionIdx];
  const subStep = section.subSteps[subStepIdx];
  const accent = sectionColorHex[section.color];
  const zusammenfassungSectionIdx = schema.sections.findIndex(
    (s) => s.key === "zusammenfassung"
  );

  /**
   * Wohin die Upload-Slots schreiben dürfen.
   *
   * Nur der Beratermodus mit bekanntem Kontakt bekommt ein Ziel; alles andere
   * — der öffentliche Token-Pfad (AP7) genauso wie ein Beratermodus ohne
   * `internalContactId` — bleibt bei der sicheren Vorgabe `readonly`. Ein
   * `mode: 'internal'` ohne Kontakt-Id gäbe es nicht: der Upload-Endpunkt
   * hängt an `contact/:contactId`.
   *
   * Zwei weitere Bedingungen, beide aus der Praxis:
   *
   *  - **`contacts:update`.** Diese Route ist nur lesegegated (`App.tsx`), der
   *    Upload-Endpunkt verlangt aber Schreibrecht — und die Nutzergruppe „darf
   *    lesen, nicht schreiben" existiert wirklich, weil
   *    `deriveMatrixReadPermissions` (`rbac.service.ts`) aus der Matrix
   *    ausschliesslich `:read` ableitet. Ohne die Prüfung stand der Knopf da,
   *    und der Guard antwortete englisch.
   *  - **Die Serverliste muss angekommen sein.** Steht sie nicht, weiss
   *    niemand, was in dem Slot wirklich liegt; ein zweiter Upload derselben
   *    Datei erzeugte dann eine Dublette in der Kundenakte.
   */
  const darfHochladen = hasPermission("contacts:update");
  const uploadZiel: UploadTarget =
    internalMode && internalContactId != null && darfHochladen && !unterlagenFehler
      ? { mode: "internal", contactId: internalContactId }
      : READONLY_UPLOAD_TARGET;

  return (
    <UploadTargetProvider target={uploadZiel}>
      <div
        className={mergeClasses(styles.shell, internalMode && styles.shellEmbedded)}
      >
        <TopBar
          customerName={companyName}
          onMenuClick={() => setMobileNavOpen((v) => !v)}
        />
        <div className={styles.body}>
          {/* Desktop sidebar — visible in both customer and advisor modes. */}
          <aside className={styles.sidebarDesktop}>
            <Sidebar
              sections={schema.sections}
              currentSectionIdx={sectionIdx}
              currentSubStepIdx={subStepIdx}
              onSelect={handleSelect}
              progressFraction={overallFraction}
              progressDetail=""
            />
          </aside>

          {mobileNavOpen && (
            <div className={styles.drawer}>
              <div
                className={styles.drawerBackdrop}
                onClick={() => setMobileNavOpen(false)}
                aria-hidden="true"
              />
              <div className={styles.drawerInner}>
                <Sidebar
                  sections={schema.sections}
                  currentSectionIdx={sectionIdx}
                  currentSubStepIdx={subStepIdx}
                  onSelect={handleSelect}
                  progressFraction={overallFraction}
                  progressDetail=""
                />
              </div>
            </div>
          )}

          <div className={styles.rightPane}>
            <main className={styles.main} ref={mainRef}>
              <div
                className={mergeClasses(styles.inner, subStep.customKind && styles.innerWide)}
              >
                <div
                  className={styles.sectionPill}
                  style={{ backgroundColor: accent }}
                >
                  {section.title}
                </div>
                <h2 className={styles.title}>{subStep.title}</h2>
                {subStep.description && (
                  <p className={styles.description}>{subStep.description}</p>
                )}
                {/*
                  Eine gelogene Dateiliste darf nicht wie eine echte aussehen.
                  Der Hinweis steht deshalb über den Fragen — nicht am Slot:
                  betroffen sind ALLE elf Slots, egal auf welchem Schritt der
                  Berater gerade ist.
                */}
                {unterlagenFehler && internalMode && (
                  <div className={styles.unterlagenHinweis} role="status">
                    <span>
                      Hochgeladene Unterlagen konnten nicht geladen werden — die
                      Anzeige der Datei-Slots ist unvollständig. Bis dahin sind
                      sie nur zum Ansehen.
                    </span>
                    <button
                      type="button"
                      className={styles.unterlagenHinweisKnopf}
                      onClick={() => void wiederholeUnterlagen()}
                      disabled={unterlagenLaedt}
                    >
                      {unterlagenLaedt ? "wird geladen…" : "Erneut versuchen"}
                    </button>
                  </div>
                )}
                <div className={styles.questions}>
                  {subStep.customKind === "summary" ? (
                    <SummaryView
                      sections={schema.sections}
                      zusammenfassungSectionIdx={zusammenfassungSectionIdx}
                      answers={answers}
                      onJumpTo={handleSelect}
                    />
                  ) : subStep.customKind === "detail-analyses" ? (
                    <DetailAnalysesView answers={answers} />
                  ) : subStep.customKind === "auswertung" ? (
                    <AuswertungAbschluss
                      answers={answers}
                      contactId={internalContactId}
                      onChange={handleAnswerChange}
                      bereitsBestaetigt={bereitsBestaetigt}
                    />
                  ) : (
                    subStep.questions.map((q, idx) => (
                      <QuestionBlock
                        key={`${subStep.key}-${idx}`}
                        question={q}
                        answers={answers}
                        onChange={handleAnswerChange}
                      />
                    ))
                  )}
                </div>
              </div>
            </main>

            <div className={styles.bottomBarPinned}>
              <BottomBar
                onPrev={goPrev}
                onNext={goNext}
                onSubmit={handleSubmit}
                onSaveDraft={handleSaveDraft}
                canPrev={canPrev}
                isLastStep={isLastStep}
                hideForward={aufAbschluss}
                submitLabel={internalMode ? "Speichern & zur Auswertung" : undefined}
                submittingLabel={internalMode ? "Speichere…" : undefined}
                submitting={submitting}
                savingDraft={savingDraft}
                draftSavedAt={internalMode ? null : draftSavedAt}
                showSaveDraft={!internalMode}
                progressLabel={`Schritt ${flatIndex + 1} von ${totalSubSteps} · ${
                  section.title
                }`}
                progressFraction={stepFraction}
              />
            </div>
          </div>
        </div>
      </div>
    </UploadTargetProvider>
  );
};

export default RisikoanalyseStepForm;
